'use server';

import { headers } from 'next/headers';
import { Resend } from 'resend';
import { contactSchema } from '@/lib/schemas/contact';
import { contactRatelimit } from '@/lib/ratelimit';
import { ContactEmail } from '@/lib/email/contact-template';

type ContactResult =
  | { ok: true; data: { id: string } | null }
  | { ok: false; error: string };

export async function submitContactForm(input: unknown): Promise<ContactResult> {
  // 1. Validate input against shared Zod schema.
  const parsed = contactSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, error: 'Please check the form and try again.' };
  }
  const data = parsed.data;

  // 2. Determine submitter IP for rate limiting.
  const headerStore = await headers();
  const forwardedFor = headerStore.get('x-forwarded-for') ?? '';
  const ip = forwardedFor.split(',')[0]?.trim() || 'anonymous';

  // 3. Rate limit by IP — 5 requests per 60 seconds.
  const rateResult = await contactRatelimit.limit(ip);
  if (!rateResult.success) {
    return { ok: false, error: 'Too many submissions. Try again in a minute.' };
  }

  // 4. Honeypot — silently succeed so bots think the form worked.
  if (data.website.length > 0) {
    return { ok: true, data: null };
  }

  // 5. Guard: env vars must be present at runtime.
  if (!process.env['RESEND_API_KEY']) {
    return { ok: false, error: 'Contact form not configured.' };
  }
  if (!process.env['RESEND_FROM_EMAIL'] || !process.env['RESEND_TO_EMAIL']) {
    return { ok: false, error: 'Contact form not configured.' };
  }

  // 6. Send email via Resend.
  try {
    const resend = new Resend(process.env['RESEND_API_KEY']);
    const { data: resendData, error: resendError } = await resend.emails.send({
      from: process.env['RESEND_FROM_EMAIL']!,
      to: process.env['RESEND_TO_EMAIL']!,
      replyTo: data.email,
      subject: `Contact: ${data.name} from ${data.company} [${data.budget}]`,
      react: ContactEmail({
        data: {
          name: data.name,
          email: data.email,
          company: data.company,
          role: data.role,
          budget: data.budget,
          message: data.message,
        },
      }),
    });

    if (resendError) {
      return {
        ok: false,
        error: "We couldn't send your message. Please email us directly.",
      };
    }

    return { ok: true, data: { id: resendData?.id ?? '' } };
  } catch {
    return {
      ok: false,
      error: "We couldn't send your message. Please email us directly.",
    };
  }
}
